import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Languages, Upload, FileText, Loader2, Download, Trash2, AlertTriangle,
  CheckCircle, Clock, X, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POPULAR_LANGUAGES = [
  '', 'Tagalog', 'Spanish', 'French', 'Mandarin Chinese', 'Cantonese',
  'Korean', 'Japanese', 'Hindi', 'Punjabi', 'Arabic', 'Portuguese',
  'Italian', 'Russian', 'Vietnamese', 'Polish', 'Greek', 'Ukrainian',
];

const ACCEPTED_MIME = 'application/pdf,image/png,image/jpeg,image/jpg,image/webp';

/**
 * TranslationPanel — shared component used by both the Agent Portal and the
 * Admin Portal. Upload a foreign-language PDF or image and get an
 * AI-translated English copy + a bilingual side-by-side PDF.
 *
 * Props:
 *   mode      'agent' | 'admin'
 *   agent     { id, name }       — required when mode === 'agent'
 *   apiUrl    optional override
 *   adminPwd  required when mode === 'admin' (X-Admin-Password header)
 */
export const TranslationPanel = ({ mode = 'agent', agent, apiUrl = API_URL, adminPwd = '' }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [forceLang, setForceLang] = useState('');
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = useCallback(() => {
    return mode === 'admin' && adminPwd ? { 'X-Admin-Password': adminPwd } : {};
  }, [mode, adminPwd]);

  const listUrl = useCallback(() => {
    if (mode === 'admin') return `${apiUrl}/api/translation/jobs`;
    return `${apiUrl}/api/translation/jobs?agent_id=${agent?.id}`;
  }, [apiUrl, mode, agent?.id]);

  const fetchJobs = useCallback(async () => {
    if (mode === 'agent' && !agent?.id) return;
    setLoading(true);
    try {
      const r = await fetch(listUrl(), { headers: authHeaders() });
      if (r.ok) setJobs(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [listUrl, authHeaders, mode, agent?.id]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      // Iter 36 — multi-file batch. The backend will merge them into one
      // translation job with per-file dividers in the output PDF.
      const isBatch = fileList.length > 1;
      if (isBatch) {
        Array.from(fileList).forEach((f) => fd.append('files', f));
      } else {
        fd.append('file', fileList[0]);
      }
      if (forceLang) fd.append('force_language', forceLang);
      if (mode === 'agent') {
        fd.append('agent_id', agent.id);
        fd.append('agent_name', agent.name || '');
      }
      const endpoint = isBatch ? '/api/translation/upload-batch' : '/api/translation/upload';
      const r = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Upload failed (${r.status})`);
      }
      const data = await r.json();
      setActiveJobId(data.job.id);
      await fetchJobs();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openJob = async (id) => {
    setActiveJobId(id);
    try {
      const url = mode === 'admin'
        ? `${apiUrl}/api/translation/${id}`
        : `${apiUrl}/api/translation/${id}?agent_id=${agent?.id}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) setActiveJob(await r.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (activeJobId) openJob(activeJobId);
    else setActiveJob(null);
     
  }, [activeJobId]);

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this translation?')) return;
    const url = mode === 'admin'
      ? `${apiUrl}/api/translation/${id}`
      : `${apiUrl}/api/translation/${id}?agent_id=${agent?.id}`;
    await fetch(url, { method: 'DELETE', headers: authHeaders() });
    if (activeJobId === id) setActiveJobId(null);
    fetchJobs();
  };

  const pdfUrl = (id) => {
    const params = mode === 'admin' ? '' : `?agent_id=${agent?.id}`;
    return `${apiUrl}/api/translation/${id}/pdf${params}`;
  };

  const downloadPdf = async (id, filename) => {
    if (mode === 'admin') {
      // X-Admin-Password header required → fetch then trigger blob download
      try {
        const r = await fetch(pdfUrl(id), { headers: authHeaders() });
        if (!r.ok) throw new Error('Download failed');
        const blob = await r.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `translated_${filename || 'document'}.pdf`;
        a.click();
      } catch (e) { alert(e.message); }
    } else {
      window.open(pdfUrl(id), '_blank', 'noopener');
    }
  };

  const statusBadge = (status) => {
    const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';
    if (status === 'done') return <span className={`${base} bg-green-100 text-green-700`}><CheckCircle className="h-3 w-3" />Done</span>;
    if (status === 'failed') return <span className={`${base} bg-red-100 text-red-700`}><AlertTriangle className="h-3 w-3" />Failed</span>;
    return <span className={`${base} bg-yellow-100 text-yellow-700`}><Clock className="h-3 w-3" />Processing</span>;
  };

  return (
    <div className="space-y-4" data-testid="translation-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-[#003D5C]" />Document Translation
            {mode === 'admin' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Admin · all advisors</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload a PDF, PNG, or JPG in any language. Our AI translator (Gemini 2.5 Pro) will extract every paragraph,
            translate it to English, and generate a side-by-side bilingual PDF you can keep on file.
          </p>

          <div className="border-2 border-dashed border-[#D4AF37] rounded-lg p-4 sm:p-6 text-center bg-amber-50/40">
            <Upload className="h-7 w-7 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-sm text-gray-700 mb-2">Drop a file here or click to select. Max 25 MB.</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
              <label className="text-xs text-gray-600">Force source language:</label>
              <select
                value={forceLang}
                onChange={(e) => setForceLang(e.target.value)}
                className="text-xs px-2 py-1 border rounded"
                data-testid="translation-force-lang"
              >
                {POPULAR_LANGUAGES.map(l => <option key={l} value={l}>{l || 'Auto-detect'}</option>)}
              </select>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIME}
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="translation-file-input"
              disabled={uploading}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] font-semibold"
              data-testid="translation-upload-btn"
            >
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Translating…</> : <><Upload className="h-4 w-4 mr-2" />Choose File(s)</>}
            </Button>
            {uploading && <p className="text-xs text-gray-500 mt-2">Multi-file batches can take 1-2 minutes. Please don't close this tab.</p>}
            {!uploading && <p className="text-xs text-gray-500 mt-2">Tip: select multiple files (Ctrl+click / Shift+click) — they'll be merged into one bilingual PDF with per-file dividers.</p>}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800 flex items-start justify-between gap-2">
              <span><AlertTriangle className="h-4 w-4 inline mr-1" />{error}</span>
              <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Translations {mode === 'admin' ? '(all)' : ''}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
            {loading && <p className="text-xs text-gray-500">Loading…</p>}
            {!loading && jobs.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No translations yet. Upload a file above to get started.</p>
            )}
            {jobs.map(j => (
              <div key={j.id} className={`group border rounded-lg flex items-stretch transition-colors ${activeJobId === j.id ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'}`} data-testid={`translation-row-${j.id}`}>
                <button
                  onClick={() => setActiveJobId(j.id)}
                  className="flex-1 text-left p-3 flex items-start gap-2 min-w-0"
                  data-testid={`translation-job-${j.id}`}
                >
                  <FileText className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{j.original_filename}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {statusBadge(j.status)}
                      {j.source_language_detected && (
                        <span className="text-xs text-gray-600">→ {j.source_language_detected}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {new Date(j.created_at).toLocaleString()}
                      {mode === 'admin' && j.user_name && ` · ${j.user_name}`}
                    </p>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteJob(j.id); }}
                  title="Delete translation"
                  className="px-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-r-lg transition-colors"
                  data-testid={`translation-row-delete-${j.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-sm truncate">
              {activeJob ? activeJob.original_filename : 'Select a translation'}
            </CardTitle>
            {activeJob?.status === 'done' && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => downloadPdf(activeJob.id, activeJob.original_filename)} data-testid="translation-download-pdf">
                  <Download className="h-3.5 w-3.5 mr-1" />Bilingual PDF
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteJob(activeJob.id)} data-testid="translation-delete">
                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
            {!activeJob && <p className="text-sm text-gray-500 text-center py-10">Pick a file on the left to view its translation.</p>}

            {activeJob?.status === 'failed' && (
              <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />Translation failed: {activeJob.error || 'Unknown error'}
              </div>
            )}

            {activeJob?.status === 'done' && (
              <>
                {/* Meta */}
                <div className="bg-gray-50 border rounded p-3 text-xs grid sm:grid-cols-2 gap-x-3 gap-y-1">
                  <div><b>Source language:</b> {activeJob.source_language_detected || '—'}</div>
                  <div><b>Pages:</b> {activeJob.page_count || '—'}</div>
                  {(activeJob.source_files || []).length > 1 && (
                    <div className="sm:col-span-2">
                      <b>Files in batch ({activeJob.source_files.length}):</b>
                      <ul className="mt-1 ml-4 list-disc">
                        {activeJob.source_files.map(sf => (
                          <li key={`sf-${sf.index}`} className={sf.error ? 'text-red-600' : ''}>
                            {sf.original_filename}
                            {sf.source_language_detected && ` (${sf.source_language_detected}`}
                            {sf.page_count ? `, ${sf.page_count} pages)` : sf.source_language_detected ? ')' : ''}
                            {sf.error && ` — failed: ${sf.error}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="sm:col-span-2"><b>Summary:</b> {activeJob.summary_en || '—'}</div>
                  {mode === 'admin' && <div className="sm:col-span-2"><b>Submitted by:</b> {activeJob.user_name} ({activeJob.user_role})</div>}
                </div>

                {/* Segments — group by source file when present */}
                <div className="space-y-2" data-testid="translation-segments">
                  {(() => {
                    const hasBatch = (activeJob.source_files || []).length > 0;
                    if (!hasBatch) {
                      // Single-file: flat list
                      const segs = activeJob.segments || [];
                      if (segs.length === 0) return <p className="text-sm text-gray-500 italic">No text segments returned.</p>;
                      return segs.map((seg, i) => (
                        <div key={`seg-${i}`} className="grid sm:grid-cols-2 gap-2 border rounded overflow-hidden">
                          <div className="p-2 bg-amber-50 text-xs whitespace-pre-wrap border-r" dir="auto">{seg.original}</div>
                          <div className="p-2 bg-white text-xs whitespace-pre-wrap">{seg.english}</div>
                        </div>
                      ));
                    }
                    // Multi-file: render per-file groups in upload order
                    const groups = activeJob.source_files
                      .slice()
                      .sort((a, b) => (a.index || 0) - (b.index || 0));
                    return groups.map(sf => {
                      const fileSegs = (activeJob.segments || []).filter(s => s.source_index === sf.index);
                      return (
                        <div key={`group-${sf.index}`} className="border-l-4 border-[#D4AF37] pl-2 space-y-2">
                          <p className="text-xs font-semibold text-[#003D5C]">
                            ── File {sf.index + 1}: {sf.original_filename}
                            {sf.source_language_detected && <span className="text-gray-500 font-normal"> ({sf.source_language_detected})</span>}
                          </p>
                          {sf.error && <p className="text-xs text-red-600">⚠ {sf.error}</p>}
                          {fileSegs.length === 0 && !sf.error && <p className="text-xs text-gray-400 italic">No segments.</p>}
                          {fileSegs.map((seg, i) => (
                            <div key={`seg-${sf.index}-${i}`} className="grid sm:grid-cols-2 gap-2 border rounded overflow-hidden">
                              <div className="p-2 bg-amber-50 text-xs whitespace-pre-wrap border-r" dir="auto">{seg.original}</div>
                              <div className="p-2 bg-white text-xs whitespace-pre-wrap">{seg.english}</div>
                            </div>
                          ))}
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}

            {activeJob?.status === 'processing' && (
              <div className="text-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Translation in progress…</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
